import { useEffect, useState } from "react";
import { ScrollView, View } from "react-native";
import type { Plan, Program } from "@heraion/shared";
import {
  LibraryBrowser,
  ProgramComposer,
  resolveTheme,
  Toast,
  useCatalogItems,
  useProgramsChangedOnUnmount,
  useProgramsInvalidator,
  useToast,
} from "@heraion/ui";

import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { confirmViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { ExercisePickerMount } from "./ExercisePickerMount";
import { useLoggerProfile } from "./logger/useLoggerProfile";
import { programCreate, type ComposerRequest, type ProgramCreate } from "./useCreateHubWeb";
import { ProfileLoadState } from "./ProfileLoadState";
import { useProfileUnitHeldWhile } from "./ThemeProfileBridge";
import { useAccountKey } from "./auth/useAccountKey";
import { useProgramEditorData } from "./useProgramEditorData";
import { useThemePalette } from "./theme";

// CO-19 - the coach experience's own Library surface (AppShell's coach
// nav tabs), web's twin of mobile's CoachLibraryScreen. `authoredAs` is
// constant "coach" throughout this file: this mount only ever renders while
// `experience === "coach"` (AppShell gates it), so there is no other view to
// disambiguate from - unlike PlanScreen's ProgramComposer mounts (athlete's
// surface, never reached from here since AppShell now routes coach's hub
// requests to "library" instead of "plan"/"workouts").
// Same self-contained edit/create-via-local-state shape as PlanScreen: rows
// picked from the LibraryBrowser below open the composer locally; the
// Create-hub's program/plan actions arrive cross-surface as a
// composerRequest (same seam PlanScreen consumes), because the hub is a
// modal over whichever tab is active.
export function CoachLibraryScreen({
  composerRequest,
  onComposerRequestHandled,
  callerSub,
  athleteRoster,
}: {
  composerRequest?: ComposerRequest | null;
  onComposerRequestHandled?: () => void;
  callerSub?: string;
  athleteRoster?: ReadonlyArray<string>;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const catalog = useCatalogItems({ catalog: exerciseCatalog });
  // #472: the follow toast, outside the list's scroll.
  const toast = useToast();
  // #336: the editors' device drafts, per account on this browser. The
  // library below remounts when an editor closes, so it refetches by itself.
  const draftScope = useAccountKey("editorDraft");

  const [editing, setEditing] = useState<Program | null>(null);
  // The program editor's way out tells the other programs lists (#380).
  const programsInvalidator = useProgramsInvalidator();
  // #336 slice 6: the program editor's progress marker and MAXes.
  const editorData = useProgramEditorData(editing?.id);
  // New program: its start (ProgramCreate), from the request or a card.
  const [creating, setCreating] = useState<ProgramCreate | null>(null);
  useProgramsChangedOnUnmount(editing !== null || creating !== null);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  // #343: units and loggerProfile are null together, until the profile is
  // read. An open editor keeps the unit it opened with, past a re-read.
  const editorOpen = editing !== null || creating !== null || editingPlan !== null || creatingPlan;
  const units = useProfileUnitHeldWhile(editorOpen);
  const loggerProfile = useLoggerProfile(editorOpen);

  // Same consume-then-clear contract as PlanScreen's composerRequest effect.
  useEffect(() => {
    if (!composerRequest) return;
    if (composerRequest.kind === "program") {
      // One editor at a time: a request replaces the open one.
      setEditing(composerRequest.program ?? null);
      setCreating(
        composerRequest.program
          ? null
          : programCreate({ model: composerRequest.model, seed: composerRequest.seed }),
      );
    } else if (composerRequest.kind === "plan") {
      if (composerRequest.plan) setEditingPlan(composerRequest.plan);
      else setCreatingPlan(true);
    }
    // "workout" requests never route here (AppShell only sends this surface
    // program/plan kinds) — nothing for this effect to do with one.
    onComposerRequestHandled?.();
  }, [composerRequest, onComposerRequestHandled]);

  // Every write below (programs, plans, a model's loads) is in the unit.
  if (units === null || loggerProfile === null) return <ProfileLoadState theme={theme} />;

  // The composers name rows from the catalog: wait for it, not «Deleted exercise».
  if ((editing || creating || editingPlan || creatingPlan) && !catalog.ready) {
    return <ProfileLoadState theme={theme} />;
  }

  if (editing || creating) {
    return (
      <ProgramComposer
        key={editing?.id ?? creating?.nonce}
        mode="prescribe"
        profile={loggerProfile}
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        program={editing ?? undefined}
        initialModel={creating?.model}
        creationSeed={creating?.seed}
        {...editorData}
        authoredAs="coach"
        catalog={catalog.items}
        exerciseCatalog={catalog.raw ?? undefined}
        Picker={ExercisePickerMount}
        unitsPreference={units}
        confirm={confirmViaWindow}
        callerSub={callerSub}
        athleteRoster={athleteRoster}
        keepOpen
        draftScope={draftScope}
        draftOwner={callerSub ?? null}
        onSaved={() => {
          setEditing(null);
          setCreating(null);
          programsInvalidator?.invalidate();
        }}
        onCancel={() => {
          setEditing(null);
          setCreating(null);
          programsInvalidator?.invalidate();
        }}
      />
    );
  }

  if (editingPlan || creatingPlan) {
    return (
      <ProgramComposer
        key={editingPlan?.id ?? "new-plan"}
        mode="single"
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        plan={editingPlan ?? undefined}
        authoredAs="coach"
        catalog={catalog.items}
        Picker={ExercisePickerMount}
        unitsPreference={units}
        confirm={confirmViaWindow}
        callerSub={callerSub}
        athleteRoster={athleteRoster}
        keepOpen
        draftScope={draftScope}
        draftOwner={callerSub ?? null}
        onSaved={() => {
          setEditingPlan(null);
          setCreatingPlan(false);
        }}
        onCancel={() => {
          setEditingPlan(null);
          setCreatingPlan(false);
        }}
      />
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: theme.palette.bg }}>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <LibraryBrowser
          theme={theme}
          dark={dark}
          apiFetch={apiFetch}
          durableWrite={durableWrite}
          catalog={catalog.raw ? [...catalog.raw] : null}
          unit={units}
          maxMeta={editorData.maxMeta}
          effortScale={loggerProfile.effortScale}
          authoredAs="coach"
          callerSub={callerSub ?? null}
          athleteRoster={athleteRoster}
          onEditProgram={setEditing}
          onEditPlan={setEditingPlan}
          onPlanFromModel={(model) => {
            setCreating(programCreate({ model }));
          }}
          onEditFromModel={(seed) => setCreating(programCreate({ seed }))}
          notify={toast.show}
        />
      </ScrollView>
      <Toast theme={theme} toast={toast.toast} onDismiss={toast.dismiss} />
      {toast.region}
    </View>
  );
}
